// Helpers for IPP tracks: latest pass of a satellite and "no live data" explanation.

/** Elevation cutoff used by the engine (dev-history engine-pipeline D10) */
export const ELEVATION_MASK_DEG = 10;

/** A gap longer than this between two points means a new pass (or a long interruption) */
export const TRACK_MAX_GAP_S = 60;

/** Parse the API `createdate` (UTC, often without timezone suffix) to epoch ms */
export function parseCreatedate(value: any): number | null {
  if (!value) return null;
  const str = String(value);
  const iso = /Z$|[+-]\d\d:?\d\d$/.test(str) ? str : str + "Z";
  const ms = Date.parse(iso);
  return isNaN(ms) ? null : ms;
}

/**
 * Points are newest-first. Keep only the latest continuous pass: stop at the first gap
 * longer than TRACK_MAX_GAP_S. Without this, a satellite that rose recently is drawn
 * together with the end of its previous pass (hours earlier), joined by a long line.
 */
export function latestPass(points: any[]): any[] {
  if (!points || points.length === 0) return [];
  const out = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const dt = points[i - 1].t - points[i].t; // GNSS seconds of week, newest-first
    if (!(dt > 0 && dt <= TRACK_MAX_GAP_S)) break; // gap, or week rollover / bad data
    out.push(points[i]);
  }
  return out;
}

export function formatAgo(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h} h ${mm} min` : `${h} h`;
}

/** Why a satellite has no live data, from its most recent point (points newest-first) */
export function noLiveReason(points: any[] | undefined): string {
  if (!points || points.length === 0) return "No data available for this satellite";
  const last = points[0];
  const ms = parseCreatedate(last.createdate);
  const ago = ms !== null ? formatAgo((Date.now() - ms) / 1000) : null;
  const ele = typeof last.ele === "number" ? last.ele : null;
  if (ele !== null && ele <= ELEVATION_MASK_DEG + 0.5) {
    return `Satellite set below the ${ELEVATION_MASK_DEG}° elevation mask` + (ago ? ` ${ago} ago` : "");
  }
  return (
    "Data interrupted" +
    (ago ? ` ${ago} ago` : "") +
    (ele !== null ? ` at ${ele.toFixed(0)}° elevation` : "") +
    " (signal loss or epoch discarded by quality checks)"
  );
}
